import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Megaphone, Plus, Trash2, Video, Sparkles, Loader2, Image as ImageIcon, X, Upload, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import SmartRichText from "@/components/SmartRichText";

const CATEGORIES = ["general", "policy", "event", "kudos", "urgent"];
// Iter 253z-10 · The hard-coded ROLES list didn't reflect a workspace's
// actual custom roles (e.g. `hr-manager`, `iter241-effort-only`), so admins
// picked a role that filtered out every real recipient. We now fetch the
// live roles list and only fall back to this seed if the roles endpoint
// hasn't responded yet.
const DEFAULT_ROLES = ["owner", "admin", "manager", "designer", "viewer", "external"];

// Iter 231 · Starter prompts for the AI composer — one click seeds a realistic
// scaffold so admins skip the empty-page paralysis. Each preset also picks a
// sensible tone.
const AI_PRESETS = [
  { key: "policy",   label: "Policy update",  icon: "📄", tone: "formal",
    hint: "Formal policy announcement",
    prompt: "Effective [date], our [policy area] policy is updated. Highlight the key change, the reason, and any action employees must take by the deadline." },
  { key: "kudos",    label: "Kudos",           icon: "🎉", tone: "celebratory",
    hint: "Celebrate a person or team",
    prompt: "Give kudos to [name/team] for [what they achieved]. Mention the impact on the company and thank them by name." },
  { key: "event",    label: "Event RSVP",      icon: "🗓️", tone: "friendly",
    hint: "Event announcement with RSVP",
    prompt: "We're hosting [event] on [date] at [time] · [location]. Explain what to expect and ask everyone to RSVP by [deadline]." },
  { key: "downtime", label: "System downtime", icon: "🛠️", tone: "urgent",
    hint: "Scheduled maintenance notice",
    prompt: "Scheduled maintenance for [system] on [date] from [start time] to [end time] UTC. State impact, workaround if any, and who to contact for issues." },
];

export default function AnnouncementDialog({ ann, departments, locations, onClose, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const isNew = !ann?.id;
  const [form, setForm] = useState({
    title: ann.title || "",
    body: ann.body || "",
    post_type: ann.post_type || "text",
    poll_options: ann.poll_options || ["", ""],
    media_url: ann.media_url || "",
    category: ann.category || "general",
    pinned: !!ann.pinned,
    comments_enabled: ann.comments_enabled !== false,
    expires_at: ann.expires_at?.slice(0, 10) || "",
    // Iter 253z-10 · Datetime-local input format: YYYY-MM-DDTHH:mm
    publish_at: ann.publish_at?.slice(0, 16) || "",
    target_roles: ann.target_roles || [],
    target_department_ids: ann.target_department_ids || [],
    target_location_ids: ann.target_location_ids || [],
    // Feb 2026 · Specific-users audience. When non-empty, this list
    // alone decides who sees the announcement (the backend ignores the
    // role / dept / location filters). Picked via the searchable
    // workspace-member chip-grid below.
    target_user_ids: ann.target_user_ids || [],
  });
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiTone, setAiTone] = useState("concise");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiImgBusy, setAiImgBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  // Iter 253z-10 · Live workspace roles for the audience picker.
  const [wsRoles, setWsRoles] = useState([]);
  // Feb 2026 · Workspace members for the specific-users audience picker.
  const [wsMembers, setWsMembers] = useState([]);
  const [memberQuery, setMemberQuery] = useState("");

  useEffect(() => {
    if (!activeWorkspaceId) return;
    let cancelled = false;
    api.get(`/workspaces/${activeWorkspaceId}/roles`)
      .then(({ data }) => {
        if (cancelled) return;
        // R158x · Feb 2026 — Hide deactivated roles from the announcement
        // audience picker so admins don't ping empty cohorts.
        const rows = (data || [])
          .filter((r) => r.is_active !== false)
          .map((r) => ({
            v: r.slug || r.id,
            label: r.name || r.slug || "role",
          }));
        setWsRoles(rows.length ? rows : DEFAULT_ROLES.map((s) => ({ v: s, label: s })));
      })
      .catch(() => setWsRoles(DEFAULT_ROLES.map((s) => ({ v: s, label: s }))));
    return () => { cancelled = true; };
  }, [activeWorkspaceId]);

  // Feb 2026 · Load active workspace members once so the "specific users"
  // picker has a stable list to filter on. One lightweight GET on dialog
  // open; the list rarely changes mid-compose.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    let cancelled = false;
    api.get(`/workspaces/${activeWorkspaceId}/members`)
      .then(({ data }) => {
        if (cancelled) return;
        const rows = (data || [])
          .filter((m) => m.status !== "removed" && m.status !== "deleted")
          .map((m) => ({
            user_id: m.user_id || m.id,
            name: m.user?.name || m.name || m.user?.email || m.email || "Unknown",
            email: m.user?.email || m.email || "",
            role: m.role || "",
          }))
          .filter((m) => !!m.user_id)
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setWsMembers(rows);
      })
      .catch(() => setWsMembers([]));
    return () => { cancelled = true; };
  }, [activeWorkspaceId]);

  const uploadCover = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file (jpg, png, gif, webp)"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Image is too large (max 15 MB)"); return; }
    setUploadBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/rich-media`, body,
        { headers: { "Content-Type": "multipart/form-data" } });
      // data.url is a relative /api/v1/rich-media/<id> path — stored as-is so
      // <img> in the announcement card renders through the same ingress.
      set("media_url", data.url);
      toast.success("Cover uploaded");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Upload failed"));
    } finally {
      setUploadBusy(false);
    }
  };
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleIn = (key, val) => setForm((s) => ({ ...s, [key]: s[key].includes(val) ? s[key].filter((x) => x !== val) : [...s[key], val] }));
  const setPollOpt = (idx, v) => setForm((s) => { const next = [...s.poll_options]; next[idx] = v; return { ...s, poll_options: next }; });
  const addPollOpt = () => setForm((s) => ({ ...s, poll_options: [...s.poll_options, ""] }));
  const removePollOpt = (idx) => setForm((s) => ({ ...s, poll_options: s.poll_options.filter((_, i) => i !== idx) }));

  const aiCompose = async () => {
    if (!aiPrompt.trim()) { toast.error("Enter a prompt"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/announcements/ai-compose`,
                                          { prompt: aiPrompt.trim(), tone: aiTone, category: form.category });
      set("title", data.title || form.title);
      set("body", data.body || form.body);
      if (data.warning) toast.warning(data.warning);
      else toast.success("AI drafted your announcement — review and edit before posting");
      setAiOpen(false);
      setAiPrompt("");
    } catch (e) {
      toast.error(apiErrorMessage(e, "AI compose failed"));
    } finally { setAiBusy(false); }
  };

  const aiComposeImage = async () => {
    const seed = (aiPrompt.trim() || form.title.trim() || form.body.trim().slice(0, 200));
    if (!seed) { toast.error("Add a prompt, title, or body first — the image uses that as context"); return; }
    setAiImgBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/announcements/ai-compose-image`,
                                          { prompt: seed });
      if (!data?.media_url) { toast.error("No image returned"); return; }
      set("media_url", data.media_url);
      toast.success("Cover image generated — remove it via the media URL field if you don't want it");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Image generation failed"));
    } finally { setAiImgBusy(false); }
  };

  const save = async () => {
    if (!form.title?.trim()) { toast.error("Title required"); return; }
    if (form.post_type !== "poll" && !form.body?.trim()) { toast.error("Body required"); return; }
    if (form.post_type === "poll" && form.poll_options.filter((o) => o.trim()).length < 2) { toast.error("Polls need at least 2 options"); return; }
    if (form.post_type === "video" && !form.media_url?.trim()) { toast.error("Video URL required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        expires_at: form.expires_at || null,
        // Iter 253z-10 · Convert datetime-local (naive) to ISO. Empty → null
        // so the backend treats the post as immediately live.
        publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : null,
        media_url: form.media_url || null,
        poll_options: form.post_type === "poll" ? form.poll_options.filter((o) => o.trim()) : null,
      };
      if (isNew) { await api.post(`/workspaces/${activeWorkspaceId}/announcements`, payload); toast.success(payload.publish_at && new Date(payload.publish_at) > new Date() ? "Scheduled" : "Posted"); }
      else { await api.patch(`/announcements/${ann.id}`, payload); toast.success("Updated"); }
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-auto" data-testid="announcement-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Post announcement" : `Edit · ${ann.title}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Title *" value={form.title} onChange={(v) => set("title", v)} testid="ann-title" />

          {/* Iter 230 · AI Compose panel */}
          <div className="border border-primary/40 bg-primary/5">
            <button
              type="button"
              onClick={() => setAiOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold hover:bg-primary/10 transition"
              data-testid="ann-ai-toggle"
            >
              <span className="inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                Compose with AI
              </span>
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                {aiOpen ? "hide" : "expand"}
              </span>
            </button>
            {aiOpen && (
              <div className="p-3 pt-0 space-y-2" data-testid="ann-ai-panel">
                {/* Iter 231 · One-click starter prompts so admins don't stare at an empty box */}
                <div className="flex flex-wrap gap-1.5">
                  {AI_PRESETS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => { setAiPrompt(p.prompt); setAiTone(p.tone || "concise"); }}
                      className="text-[10px] px-2 py-1 border border-border hover:border-primary hover:bg-primary/5 transition inline-flex items-center gap-1"
                      data-testid={`ann-ai-preset-${p.key}`}
                      title={p.hint}
                    >
                      {p.icon} {p.label}
                    </button>
                  ))}
                </div>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Tell AI what to announce. e.g. 'Company off-site next Friday in Bengaluru, everyone must confirm attendance by Wednesday.'"
                  rows={3}
                  className="text-sm"
                  data-testid="ann-ai-prompt"
                />
                <div className="flex items-end gap-2">
                  <div className="w-48">
                    <Label className="block text-[10px] uppercase tracking-wide mb-1 text-muted-foreground">Tone</Label>
                    <Select value={aiTone} onValueChange={setAiTone}>
                      <SelectTrigger className="h-9 text-sm" data-testid="ann-ai-tone"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="concise">Concise</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                        <SelectItem value="celebratory">Celebratory</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={aiCompose}
                    disabled={aiBusy || !aiPrompt.trim()}
                    className="rounded-none h-9 ml-auto"
                    data-testid="ann-ai-generate"
                  >
                    {aiBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                    {aiBusy ? "Composing…" : (form.title || form.body ? "Overwrite title + body" : "Generate")}
                  </Button>
                  <Button
                    onClick={aiComposeImage}
                    disabled={aiImgBusy}
                    variant="outline"
                    className="rounded-none h-9"
                    data-testid="ann-ai-generate-image"
                    title="Generate a cover banner with Nano Banana (Gemini)"
                  >
                    {aiImgBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5 mr-1.5" />}
                    {aiImgBusy ? "Painting…" : "+ Image"}
                  </Button>
                </div>
                {form.media_url && form.post_type !== "video" && (
                  <div className="text-[10px] text-muted-foreground" data-testid="ann-ai-image-generated-hint">
                    Cover image ready — preview and remove options are in the &quot;Cover image&quot; section below.
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground">
                  Powered by Claude Sonnet 4.5 · Cover images by Nano Banana. Always review before posting.
                </p>
              </div>
            )}
          </div>

          {/* Iter 235 · Cover image (upload OR paste URL) — separate from AI so
              admins who prefer their own artwork aren't forced into the AI flow. */}
          {form.post_type !== "video" && (
            <div className="border border-border">
              <div className="px-3 py-2 border-b border-border flex items-center gap-2 bg-muted/30">
                <ImageIcon className="w-4 h-4" />
                <span className="text-sm font-semibold">Cover image</span>
                {form.media_url && (
                  <button
                    type="button"
                    onClick={() => set("media_url", "")}
                    className="ml-auto inline-flex items-center gap-1 text-[10px] text-rose-600 hover:underline"
                    data-testid="ann-cover-remove"
                  >
                    <X className="w-3 h-3" /> Remove
                  </button>
                )}
              </div>
              <div className="p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <label
                    className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border cursor-pointer transition ${uploadBusy ? "border-border text-muted-foreground" : "border-border hover:border-primary hover:bg-primary/5"}`}
                    data-testid="ann-cover-upload-label"
                  >
                    {uploadBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {uploadBusy ? "Uploading…" : "Upload image"}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadCover(f); }}
                      disabled={uploadBusy}
                      data-testid="ann-cover-upload-input"
                    />
                  </label>
                  <span className="text-[10px] text-muted-foreground">or paste a URL below</span>
                </div>
                <Input
                  value={form.media_url || ""}
                  onChange={(e) => set("media_url", e.target.value)}
                  placeholder="https://…/image.jpg (or upload above / generate with AI)"
                  className="h-9 rounded-none text-xs"
                  data-testid="ann-cover-url-input"
                />
                {form.media_url && (
                  <div className="border border-border" data-testid="ann-cover-preview">
                    <img src={form.media_url} alt="Cover preview" className="block w-full max-h-48 object-cover" />
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground">
                  Max 15 MB · JPG, PNG, GIF, WebP. Upload lives in your workspace media store — the image URL will stay valid.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-4 gap-2">
            <PostTypeBtn type="text" current={form.post_type} onClick={() => set("post_type", "text")} label="Text" icon={Megaphone} />
            <PostTypeBtn type="blog" current={form.post_type} onClick={() => set("post_type", "blog")} label="Blog (rich)" icon={Megaphone} />
            <PostTypeBtn type="poll" current={form.post_type} onClick={() => set("post_type", "poll")} label="Poll" icon={Megaphone} />
            <PostTypeBtn type="video" current={form.post_type} onClick={() => set("post_type", "video")} label="Video" icon={Video} />
          </div>
          {form.post_type === "blog" ? (
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Body * (rich text)</Label>
              <SmartRichText value={form.body} onChange={(html) => set("body", html)} placeholder="Write a rich post. Use `/` for headings, lists, images, or `/ai` to polish." mediaMode="upload" workspaceId={activeWorkspaceId} minRows={6} maxHeight={500} testid="ann-body-rich" />
            </div>
          ) : (
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">{form.post_type === "poll" ? "Poll question / context" : "Body *"}</Label>
              <Textarea value={form.body} onChange={(e) => set("body", e.target.value)} rows={form.post_type === "poll" ? 2 : 5} data-testid="ann-body" placeholder={form.post_type === "poll" ? "What's the question?" : ""} />
            </div>
          )}
          {form.post_type === "poll" && (
            <div className="border border-border p-3 space-y-2" data-testid="ann-poll-options">
              <div className="label-eyebrow">Poll options *</div>
              {form.poll_options.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input value={opt} onChange={(e) => setPollOpt(idx, e.target.value)} placeholder={`Option ${idx + 1}`} className="h-9 text-sm" data-testid={`poll-opt-${idx}`} />
                  {form.poll_options.length > 2 && <Button size="sm" variant="ghost" className="rounded-none" onClick={() => removePollOpt(idx)} data-testid={`poll-opt-remove-${idx}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                </div>
              ))}
              <Button size="sm" variant="outline" className="rounded-none" onClick={addPollOpt} data-testid="poll-add-opt"><Plus className="w-3.5 h-3.5 mr-1" /> Option</Button>
            </div>
          )}
          {form.post_type === "video" && (
            <Field label="Video URL * (mp4 / webm / hosted link)" value={form.media_url} onChange={(v) => set("media_url", v)} testid="ann-media-url" />
          )}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="h-10" data-testid="ann-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field label="Expires on" type="date" value={form.expires_at} onChange={(v) => set("expires_at", v)} testid="ann-expires" />
            <label className="inline-flex items-end gap-2 text-sm pb-2">
              <input type="checkbox" checked={form.pinned} onChange={(e) => set("pinned", e.target.checked)} data-testid="ann-pinned" />
              Pin to top
            </label>
          </div>
          {/* Iter 253z-10 · Schedule for future publication. Leave empty to post immediately. */}
          <div>
            <Label className="block mb-1.5 text-sm font-semibold inline-flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5" />
              Schedule (optional)
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="datetime-local"
                value={form.publish_at}
                onChange={(e) => set("publish_at", e.target.value)}
                className="h-10 max-w-xs"
                data-testid="ann-publish-at"
              />
              {form.publish_at && (
                <button type="button" onClick={() => set("publish_at", "")}
                  className="text-[10px] px-2 py-1 border border-border hover:border-rose-400 hover:text-rose-600"
                  data-testid="ann-publish-at-clear">
                  Clear
                </button>
              )}
              <span className="text-[10px] text-muted-foreground">
                {form.publish_at
                  ? (new Date(form.publish_at) > new Date()
                      ? `Will publish on ${new Date(form.publish_at).toLocaleString()}`
                      : "Publish time is in the past — will go live immediately")
                  : "Leave blank to publish immediately"}
              </span>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.comments_enabled} onChange={(e) => set("comments_enabled", e.target.checked)} data-testid="ann-comments-enabled" />
            Allow comments
          </label>
          <div className="border border-border p-3 space-y-2">
            <div className="label-eyebrow">Audience (leave all empty = everyone)</div>
            <ChipList label="Roles" items={wsRoles.length ? wsRoles : DEFAULT_ROLES} selected={form.target_roles} onToggle={(v) => toggleIn("target_roles", v)} testid="ann-roles" />
            <ChipList label="Departments" items={departments.map((d) => ({ v: d.id, label: d.name }))} selected={form.target_department_ids} onToggle={(v) => toggleIn("target_department_ids", v)} testid="ann-depts" />
            <ChipList label="Locations" items={locations.map((l) => ({ v: l.id, label: l.name }))} selected={form.target_location_ids} onToggle={(v) => toggleIn("target_location_ids", v)} testid="ann-locations" />
            {/* Feb 2026 · Specific-users picker. When any user is picked,
                the backend sends the announcement ONLY to the picked
                users (role / department / location filters above are
                ignored). The banner below surfaces this exclusive
                mode so admins don't double-filter by mistake. */}
            <SpecificUsersPicker
              members={wsMembers}
              selected={form.target_user_ids}
              onToggle={(uid) => toggleIn("target_user_ids", uid)}
              onClear={() => set("target_user_ids", [])}
              query={memberQuery}
              setQuery={setMemberQuery}
            />
            {form.target_user_ids.length > 0 && (
              <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1"
                    data-testid="ann-specific-users-notice">
                This announcement will reach <b>only the {form.target_user_ids.length} user{form.target_user_ids.length === 1 ? "" : "s"} picked above</b>.
                Role / department / location filters are ignored in this mode.
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="ann-save">{busy ? "Saving…" : isNew ? "Post announcement" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PostTypeBtn({ type, current, onClick, label, icon: Icon }) {
  const on = current === type;
  return (
    <button type="button" onClick={onClick} className={`border px-3 py-2 text-xs inline-flex items-center justify-center gap-1.5 ${on ? "border-primary bg-primary/10 font-bold" : "border-border"}`} data-testid={`ann-post-type-${type}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function SpecificUsersPicker({ members, selected, onToggle, onClear, query, setQuery }) {
  // Feb 2026 · Searchable chip-grid for specific-user audience. The
  // top row shows the picked people as removable chips so the admin
  // can see who is on the list without scrolling the dropdown.
  const q = (query || "").trim().toLowerCase();
  const selectedSet = new Set(selected || []);
  const picked = members.filter((m) => selectedSet.has(m.user_id));
  const unpicked = members.filter((m) => !selectedSet.has(m.user_id));
  const results = (q
    ? unpicked.filter((m) =>
        m.name.toLowerCase().includes(q)
        || m.email.toLowerCase().includes(q)
        || (m.role || "").toLowerCase().includes(q))
    : unpicked
  ).slice(0, 60);
  return (
    <div data-testid="ann-specific-users">
      <div className="text-[10px] text-muted-foreground mb-1 flex items-center gap-2">
        <span>Specific users</span>
        {picked.length > 0 && (
          <>
            <span className="text-foreground font-semibold">· {picked.length} picked</span>
            <button type="button" onClick={onClear}
                     className="text-primary hover:underline normal-case"
                     data-testid="ann-specific-users-clear">
              clear
            </button>
          </>
        )}
      </div>
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {picked.map((m) => (
            <button key={m.user_id} type="button" onClick={() => onToggle(m.user_id)}
                     className="chip chip-approved text-[10px] inline-flex items-center gap-1"
                     data-testid={`ann-user-chip-${m.user_id}`}
                     title={`Remove ${m.name}`}>
              ✓ {m.name} <span aria-hidden="true" className="opacity-60">×</span>
            </button>
          ))}
        </div>
      )}
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={members.length ? "Search a teammate by name, email or role…" : "Loading members…"}
        className="w-full h-7 text-[11px] border border-border rounded-none px-2"
        data-testid="ann-specific-users-search"
        disabled={!members.length}
      />
      <div className="max-h-32 overflow-y-auto border border-border mt-1">
        {results.length === 0 ? (
          <div className="text-[10px] text-muted-foreground italic px-2 py-1.5">
            {q ? "No teammate matches that search." : members.length ? "Everyone is already picked." : "No members loaded."}
          </div>
        ) : (
          results.map((m) => (
            <button key={m.user_id} type="button" onClick={() => onToggle(m.user_id)}
                     className="w-full text-left px-2 py-1 text-[11px] hover:bg-muted/50 flex items-center gap-2"
                     data-testid={`ann-user-option-${m.user_id}`}>
              <span className="flex-1 truncate">{m.name}</span>
              <span className="text-[9px] text-muted-foreground truncate max-w-[150px]">{m.email}</span>
              {m.role && (
                <span className="text-[9px] uppercase tracking-widest text-muted-foreground">{m.role}</span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function ChipList({ label, items, selected, onToggle, testid }) {
  if (items.length === 0) return (
    <div data-testid={testid}>
      <div className="text-[10px] text-muted-foreground mb-1">{label}</div>
      <div className="text-[10px] text-muted-foreground italic">(none configured)</div>
    </div>
  );
  return (
    <div data-testid={testid}>
      <div className="text-[10px] text-muted-foreground mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {items.map((it) => {
          const v = typeof it === "string" ? it : it.v;
          const lbl = typeof it === "string" ? it : it.label;
          const on = selected.includes(v);
          return (
            <button key={v} type="button" onClick={() => onToggle(v)}
              className={`chip text-[10px] ${on ? "chip-approved" : ""}`}
              data-testid={`${testid}-${v}`}>
              {on ? "✓ " : ""}{lbl}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={testid} />
    </div>
  );
}
